<script setup lang="ts">
import { router, useForm } from '@inertiajs/vue3'
import { ExternalLink } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatAmount, formatDate, formatDateTime } from '@/lib/utils'
import {
  loanApplicationBankStatementAnalysesPath,
  loanApplicationBankStatementAnalysisPath,
} from '@/routes'

type Severity = 'red' | 'amber' | 'green'

interface EvidenceRow {
  date: string | null
  description: string | null
  amount: number | null
  credit_debit: string
}
interface Flag {
  key: string
  label: string
  severity: Severity
  display: string
  detail: string
  threshold: { amber: number; red: number; lower_is_worse?: boolean }
  evidence: EvidenceRow[]
}
interface Metric {
  ref: string
  description: string | null
  raw: string
  number: number | null
}
interface Account {
  bsb: string | null
  number: string | null
  name: string | null
  available_balance: number | null
  current_balance: number | null
  total_debits: number | null
  total_credits: number | null
}
interface Analysis {
  id: number
  institution: string | null
  account_holder: string | null
  period_start: string | null
  period_end: string | null
  period_days: number | null
  overall_severity: Severity
  analyzed_at: string
  accounts: Account[]
  metrics: Record<string, Metric>
  flags: Flag[]
  source_html_url: string | null
}

const props = defineProps<{
  applicationId: string
  analyses: Analysis[]
  readonly?: boolean
}>()

const SEVERITY_RANK: Record<Severity, number> = { red: 0, amber: 1, green: 2 }

const severityBadge: Record<Severity, string> = {
  red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  green: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
}
const severityDot: Record<Severity, string> = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  green: 'bg-green-500',
}
const severityLabel: Record<Severity, string> = {
  red: 'High concern',
  amber: 'Review',
  green: 'Clear',
}

function sortedFlags(flags: Flag[]) {
  return [...flags].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
}
function redCount(flags: Flag[]) {
  return flags.filter((f) => f.severity === 'red').length
}
function amberCount(flags: Flag[]) {
  return flags.filter((f) => f.severity === 'amber').length
}

// Curated metrics worth surfacing in the grid, in display order.
const METRIC_ORDER = [
  'DM001', 'DM002', 'DM003', 'DM013', 'DM028',
  'CF005', 'CF006', 'DM004', 'DM006', 'MN002', 'MN003',
]
function orderedMetrics(metrics: Record<string, Metric>) {
  const present = METRIC_ORDER.filter((ref) => metrics[ref])
  const rest = Object.keys(metrics).filter((ref) => !METRIC_ORDER.includes(ref))
  return [...present, ...rest].map((ref) => metrics[ref])
}

// ── Upload / analyse ────────────────────────────────────────────────────────
const uploadForm = useForm({ file: null as File | null })
const fileError = ref<string | null>(null)

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  fileError.value = null
  uploadForm.file = null
  if (!file) return
  const isHtml =
    file.type.includes('html') || /\.html?$/i.test(file.name)
  if (!isHtml) {
    fileError.value = 'Please upload the bank statement HTML file.'
    input.value = ''
    return
  }
  uploadForm.file = file
}

function submit() {
  if (!uploadForm.file) return
  uploadForm.post(loanApplicationBankStatementAnalysesPath(props.applicationId), {
    preserveScroll: true,
    onSuccess: () => uploadForm.reset(),
  })
}

function remove(id: number) {
  if (!confirm('Remove this analysis?')) return
  router.delete(loanApplicationBankStatementAnalysisPath(props.applicationId, id), {
    preserveScroll: true,
  })
}

const hasAnalyses = computed(() => props.analyses.length > 0)

// ── Source preview dialog ────────────────────────────────────────────────────
// The raw HTML can't be embedded via its ActiveStorage blob URL — Rails forces
// text/html blobs to download (XSS-prevention default), so the dialog points at
// a dedicated controller action that serves the content directly instead. The
// iframe itself is sandboxed (no allow-same-origin) as the actual security
// boundary, since this is vendor-supplied HTML with embedded JS.
const previewAnalysis = ref<Analysis | null>(null)
const previewSourceUrl = computed(() =>
  previewAnalysis.value
    ? loanApplicationBankStatementAnalysisPath(props.applicationId, previewAnalysis.value.id)
    : null,
)
function openSourcePreview(a: Analysis) {
  previewAnalysis.value = a
}
function closeSourcePreview() {
  previewAnalysis.value = null
}
</script>

<template>
  <div class="space-y-6">
    <!-- ── Upload bar ─────────────────────────────────────────────────────── -->
    <div
      v-if="!readonly"
      class="bg-muted/40 flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3"
    >
      <div class="text-sm">
        <p class="font-medium">Analyse bank statement</p>
        <p class="text-muted-foreground">
          Upload the <span class="font-mono">.html</span> statement to summarise conduct.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <input
          type="file"
          accept=".html,text/html"
          class="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-primary-foreground"
          @change="onFileChange"
        />
        <Button :disabled="!uploadForm.file || uploadForm.processing" @click="submit">
          {{ uploadForm.processing ? 'Analysing…' : 'Analyse' }}
        </Button>
      </div>
    </div>
    <p v-if="fileError" class="text-sm text-red-600">{{ fileError }}</p>

    <!-- ── Placeholder thresholds notice ──────────────────────────────────── -->
    <p v-if="hasAnalyses" class="text-muted-foreground text-xs">
      Flags are advisory. Thresholds are configurable and pending office sign-off — they
      inform the decision, they don't make it.
    </p>

    <!-- ── Empty state ────────────────────────────────────────────────────── -->
    <Card v-if="!hasAnalyses">
      <CardContent class="text-muted-foreground py-10 text-center text-sm">
        No bank statement analysed yet.
      </CardContent>
    </Card>

    <!-- ── One block per analysed statement ───────────────────────────────── -->
    <div v-for="a in analyses" :key="a.id" class="space-y-4">
      <!-- Header + overall RAG -->
      <Card>
        <CardHeader class="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle class="flex items-center gap-2">
              <span class="inline-block h-2.5 w-2.5 rounded-full" :class="severityDot[a.overall_severity]" />
              {{ a.institution }} — {{ a.account_holder }}
            </CardTitle>
            <p class="text-muted-foreground mt-1 text-sm">
              {{ a.period_start ? formatDate(a.period_start) : '—' }}
              to {{ a.period_end ? formatDate(a.period_end) : '—' }}
              ({{ a.period_days }} days) · {{ a.accounts.length }} account(s) ·
              analysed {{ formatDateTime(a.analyzed_at) }}
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <Badge :class="severityBadge[a.overall_severity]">
              {{ severityLabel[a.overall_severity] }} ·
              {{ redCount(a.flags) }} red / {{ amberCount(a.flags) }} amber
            </Badge>
            <button
              v-if="a.source_html_url"
              type="button"
              class="text-primary text-sm underline"
              @click="openSourcePreview(a)"
            >Source</button>
            <Button v-if="!readonly" variant="ghost" size="sm" @click="remove(a.id)">Remove</Button>
          </div>
        </CardHeader>
      </Card>

      <!-- Flag cards -->
      <div class="grid gap-3 sm:grid-cols-2">
        <Collapsible
          v-for="flag in sortedFlags(a.flags)"
          :key="flag.key"
          as="div"
          class="rounded-lg border"
        >
          <div class="flex items-center justify-between gap-3 p-3">
            <div class="flex items-center gap-2.5">
              <span class="inline-block h-2 w-2 shrink-0 rounded-full" :class="severityDot[flag.severity]" />
              <div>
                <p class="text-sm font-medium">{{ flag.label }}</p>
                <p class="text-muted-foreground text-xs">{{ flag.detail }}</p>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <Badge :class="severityBadge[flag.severity]">{{ flag.display }}</Badge>
              <CollapsibleTrigger
                v-if="flag.evidence.length"
                class="text-muted-foreground hover:text-foreground text-xs underline"
              >
                {{ flag.evidence.length }} txn
              </CollapsibleTrigger>
            </div>
          </div>
          <CollapsibleContent v-if="flag.evidence.length" class="border-t px-3 py-2">
            <table class="w-full text-xs">
              <tbody>
                <tr v-for="(row, i) in flag.evidence" :key="i" class="border-b last:border-0">
                  <td class="py-1 pr-2 whitespace-nowrap tabular-nums">
                    {{ row.date ? formatDate(row.date) : '—' }}
                  </td>
                  <td class="py-1 pr-2">{{ row.description }}</td>
                  <td
                    class="py-1 text-right tabular-nums"
                    :class="row.credit_debit === 'credit' ? 'text-green-600' : ''"
                  >
                    {{ row.credit_debit === 'credit' ? '+' : '−' }}{{ formatAmount(row.amount ?? 0) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <!-- Accounts + metrics -->
      <div class="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle class="text-sm">Accounts</CardTitle></CardHeader>
          <CardContent class="space-y-2">
            <div
              v-for="(acc, i) in a.accounts"
              :key="i"
              class="flex items-center justify-between border-b pb-2 text-sm last:border-0 last:pb-0"
            >
              <div>
                <p class="font-medium">{{ acc.name }}</p>
                <p class="text-muted-foreground font-mono text-xs">{{ acc.bsb }} {{ acc.number }}</p>
              </div>
              <span class="tabular-nums" :class="(acc.current_balance ?? 0) < 0 ? 'text-red-600' : ''">
                {{ formatAmount(acc.current_balance ?? 0) }}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle class="text-sm">Key metrics</CardTitle></CardHeader>
          <CardContent>
            <dl class="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm divide-y divide-muted-foreground/20">
              <template v-for="m in orderedMetrics(a.metrics)" :key="m.ref">
                <dt class="text-muted-foreground truncate" :title="m.description ?? m.ref">
                  {{ m.description ?? m.ref }}
                </dt>
                <dd class="text-right font-medium tabular-nums">{{ m.raw }}</dd>
              </template>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>

    <!-- ── Source preview dialog ────────────────────────────────────────────── -->
    <Dialog :open="previewAnalysis !== null" @update:open="(v) => { if (!v) closeSourcePreview() }">
      <DialogContent class="flex max-h-[90vh] flex-col sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle class="truncate pr-6">
            {{ previewAnalysis?.institution }} — {{ previewAnalysis?.account_holder }}
          </DialogTitle>
        </DialogHeader>

        <div class="bg-muted min-h-0 flex-1 overflow-hidden rounded-md border">
          <iframe
            v-if="previewSourceUrl"
            :src="previewSourceUrl"
            sandbox="allow-scripts"
            class="h-full min-h-[70vh] w-full"
            frameborder="0"
          />
        </div>

        <a
          v-if="previewAnalysis?.source_html_url"
          :href="previewAnalysis.source_html_url"
          target="_blank"
          class="text-primary inline-flex items-center gap-1 text-xs hover:underline"
        >
          <ExternalLink class="size-3" />
          Download source HTML
        </a>
      </DialogContent>
    </Dialog>
  </div>
</template>
